import { Component, computed, OnDestroy, OnInit, Signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { ValidationErrorsComponent } from '../../../../../common/validation-errors/validation-errors.component';
import { SpinnerComponent } from '../../../../../common/spinner/spinner.component';
import { ApplicationDataForm } from './forms/application-data.form';
import {
  ApplicationAppEntityApplicationRead,
  DocumentReceiptMethodAppEntityDictionaryDocumentReceiptMethod,
  DocumentReceiptMethodService
} from '../../../../../openapi';
import { selectApplication, selectLoading, selectPending } from '../../../../../store/selectors/application.selectors';
import { Subscription } from 'rxjs';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { updateApplication } from '../../../../../store/actions/application.actions';
import { NgxMaskDirective } from 'ngx-mask';
import { StagesComponent } from '../../../../../common/stages/stages.component';
import { AlertComponent } from '../../../../../common/alert/alert.component';
import { DatePickerComponent } from '../../../../../common/date-picker/date-picker.component';
import { ManagerFormComponent } from './components/manager-form/manager-form.component';
import {
  ApplicationRoadOccupationPurposeLabelPipe
} from '../../../../../pipes/application/application-road-occupation-purpose-label.pipe';

export interface ApplicationDataComponentConfig {
  stages: string[];
  successUrl: string;
  cancelUrl: string;
  addResponsiblePersonControls?: boolean;
  addCommentsControl?: boolean;
  addManagerForm?: boolean;
  addDeviceNameControl?: boolean;
  addDeviceTypeControl?: boolean;
  addApplicationConcernsStreetControl?: boolean;
  addAgreementControls?: boolean;
  addRoadTrafficImpactControl?: boolean;
  addObjectNameControl?: boolean;
  addExitClassControl?: boolean;
  addCorrespondenceControls?: boolean;
  addLegalInterestControl?: boolean;
}

@Component({
  selector: 'app-application-data-section',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ValidationErrorsComponent,
    SpinnerComponent,
    NgxMaskDirective,
    StagesComponent,
    AlertComponent,
    DatePickerComponent,
    ManagerFormComponent,
    ApplicationRoadOccupationPurposeLabelPipe
  ],
  providers: [
    ApplicationDataForm
  ],
  templateUrl: './application-data-section.component.html'
})
export class ApplicationDataSectionComponent implements OnInit, OnDestroy {

  public stages: string[] = this.route.snapshot.data['stages'] || [];

  public applicationId: string = this.route.snapshot.params['appId'];

  public successUrl: string = this.route.snapshot.data['successUrl'];

  public cancelUrl: string = this.route.snapshot.data['cancelUrl'];

  public application: Signal<ApplicationAppEntityApplicationRead | null> = this.store$.selectSignal(selectApplication);

  public pending: Signal<boolean> = this.store$.selectSignal(selectPending);

  public loading: Signal<boolean> = this.store$.selectSignal(selectLoading);

  private fetchApplication: Subscription = toObservable(this.application).subscribe(app => {
    if (app) {
      this.form.patchFormValues(app);
    }
  });

  private methods = toSignal(this.documentReceiptMethodService.getDocumentReceiptMethodCollectionDocumentReceiptMethodCollection({
    pagination: false
  }));

  public receiptMethods: Signal<DocumentReceiptMethodAppEntityDictionaryDocumentReceiptMethod[] | undefined> = computed(() => {
    const application = this.application();
    const methods = this.methods();
    if (!!application && !!methods?.length) {
      return application?.isFiledByProxy ? methods : methods.filter(v => !['0e62487f-24c1-4b5f-8a89-9f9129b3d727', 'cf926de7-379a-42fa-a4ec-7b398c984b30'].includes(v?.id || ''));
    } else {
      return [];
    }
  });

  public showCategoryInfo: Signal<boolean> = computed(() => {
    const symbols = ['K_I', 'K_II'];
    const purposes: string[] = this.application()?.service?.roadOccupationPurposes?.map((purpose) => purpose.symbol || '') || [];
    return !!purposes.filter(p => symbols.includes(p)).length;
  });

  constructor(
    public form: ApplicationDataForm,
    private documentReceiptMethodService: DocumentReceiptMethodService,
    private store$: Store,
    private route: ActivatedRoute,
    private router: Router
  ) {
  }

  public onCancel(): void {
    this.router.navigate([this.cancelUrl], { relativeTo: this.route });
  }

  public submitAndGoNext(): void {
    if (this.form.valid) {
      this.store$.dispatch(updateApplication({
        id: this.applicationId || '',
        application: {
          ...this.form.toWriteModel(),
          roadOccupationPurpose: this.form.toWriteModel()?.roadOccupationPurpose || this.application()?.service?.roadOccupationPurposes?.at(0)?.id
        },
        successFnc: (applicationId: string) => {
          this.router.navigate(
            [this.successUrl],
            {
              relativeTo: this.route
            }
          );
        },
        failureFnc: () => {
        }
      }));
    } else {
      this.form.markAllAsTouched();
    }
  }

  public ngOnInit(): void {
    if (!!this.route.snapshot.data['addResponsiblePersonControls']) {
      this.form.createResponsiblePersonControls();
    }
    if (!!this.route.snapshot.data['addCommentsControl']) {
      this.form.createCommentsControl();
    }
    if (!!this.route.snapshot.data['addManagerForm']) {
      this.form.createManagerForm();
    }
    if (!!this.route.snapshot.data['addDeviceTypeControl']) {
      this.form.createDeviceTypeControl();
    }
    if (!!this.route.snapshot.data['addApplicationConcernsStreetControl']) {
      this.form.createApplicationConcernsStreetControl();
    }
    if (!!this.route.snapshot.data['addAgreementControls']) {
      this.form.createAgreementControls();
    }
    if (!!this.route.snapshot.data['addRoadTrafficImpactControl']) {
      this.form.createRoadTrafficImpactControl();
    }
    if (!!this.route.snapshot.data['addDeviceNameControl']) {
      this.form.createDeviceNameControl();
    }
    if (!!this.route.snapshot.data['addObjectNameControl']) {
      this.form.createObjectNameControl();
    }
    if (!!this.route.snapshot.data['addExitClassControl']) {
      this.form.createExitClassControl();
    }
    if (!!this.route.snapshot.data['addCorrespondenceControls']) {
      this.form.createCorrespondenceControls();
    }
    if (!!this.route.snapshot.data['addLegalInterestControl']) {
      this.form.createLegalInterestControl();
    }
  }

  public ngOnDestroy(): void {
    this.fetchApplication.unsubscribe();
  }
}
